import type {
  AuditEvent,
  Container,
  Deployment,
  Fleet,
  FleetNode,
  Instance,
  Project,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  Stack,
  User,
} from "./types";

const NOW = Date.now();
const hoursAgo = (h: number) => new Date(NOW - h * 60 * 60 * 1000).toISOString();
const daysAgo = (d: number) => hoursAgo(d * 24);
const minutesAgo = (m: number) => new Date(NOW - m * 60 * 1000).toISOString();

export const FLEETS: Fleet[] = [
  { id: "fleet-prod", name: "Production", description: "Customer-facing, 3 nodes" },
  { id: "fleet-staging", name: "Staging", description: "Pre-release verification" },
  { id: "fleet-homelab", name: "Homelab", description: "Founder's NUC — dogfooding" },
];

export const NODES: FleetNode[] = [
  {
    id: "node-prod-web-1",
    name: "prod-web-1",
    hostname: "web-1.prod.internal",
    status: "online",
    agentVersion: "0.4.2",
    fleetId: "fleet-prod",
    containerCount: 4,
    lastSeenAt: minutesAgo(1),
    tags: ["role:web", "region:us-east"],
  },
  {
    id: "node-prod-db-1",
    name: "prod-db-1",
    hostname: "db-1.prod.internal",
    status: "online",
    agentVersion: "0.4.2",
    fleetId: "fleet-prod",
    containerCount: 3,
    lastSeenAt: minutesAgo(2),
    tags: ["role:observability", "region:us-east"],
  },
  {
    id: "node-prod-edge-1",
    name: "prod-edge-1",
    hostname: "edge-1.prod.internal",
    status: "offline",
    agentVersion: "0.3.9",
    fleetId: "fleet-prod",
    containerCount: 1,
    lastSeenAt: hoursAgo(6),
    tags: ["role:edge", "region:eu-west"],
  },
  {
    id: "node-staging-1",
    name: "staging-1",
    hostname: "staging-1.internal",
    status: "online",
    agentVersion: "0.4.2",
    fleetId: "fleet-staging",
    containerCount: 2,
    lastSeenAt: minutesAgo(4),
    tags: ["role:web"],
  },
  {
    id: "node-homelab-nuc",
    name: "homelab-nuc",
    hostname: "nuc.lan",
    status: "unknown",
    agentVersion: null,
    fleetId: "fleet-homelab",
    containerCount: 0,
    lastSeenAt: null,
    tags: [],
  },
];

const STOREFRONT_COMPOSE = `services:
  web:
    image: nginx:1.27
    ports:
      - "8080:80"
    depends_on:
      - api
  api:
    image: ghcr.io/acme/api:2.4.1
    environment:
      - PORT=3000
    ports:
      - "3000:3000"
  worker:
    image: ghcr.io/acme/worker:2.4.1
`;

const OBSERVABILITY_COMPOSE = `services:
  prometheus:
    image: prom/prometheus:v2.53.0
    ports:
      - "9090:9090"
  grafana:
    image: grafana/grafana:11.1.0
    ports:
      - "3001:3000"
  loki:
    image: grafana/loki:3.1.0
`;

const EDGE_PROXY_COMPOSE = `services:
  traefik:
    image: traefik:v3.1
    ports:
      - "443:443"
`;

/**
 * File-native by design (charter C-9): `compose` is the canonical text the
 * server would store verbatim. Shape mirrors `stackJSON`
 * (server/internal/stacksvc/http.go) exactly — no `serviceCount`/
 * `sourcePath`/`gitRepo`/`nodeName` on the wire; the mock resolves a node's
 * display name from `NODES` the same way the real UI joins against
 * `useNodes`, never by storing it redundantly on the stack itself.
 */
export const STACKS: Stack[] = [
  {
    id: "stack-storefront",
    name: "storefront",
    status: "deployed",
    fleetId: "fleet-prod",
    nodeId: "node-prod-web-1",
    compose: STOREFRONT_COMPOSE,
    createdBy: "founder",
    createdAt: daysAgo(12),
    updatedAt: hoursAgo(9),
  },
  {
    id: "stack-observability",
    name: "observability",
    status: "deployed",
    fleetId: "fleet-prod",
    nodeId: "node-prod-db-1",
    compose: OBSERVABILITY_COMPOSE,
    createdBy: "founder",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(2),
  },
  {
    id: "stack-edge-proxy",
    name: "edge-proxy",
    status: "error",
    fleetId: "fleet-prod",
    nodeId: "node-prod-edge-1",
    compose: EDGE_PROXY_COMPOSE,
    createdBy: "founder",
    createdAt: daysAgo(10),
    updatedAt: hoursAgo(6),
  },
  {
    id: "stack-storefront-staging",
    name: "storefront-staging",
    status: "deployed",
    fleetId: "fleet-staging",
    nodeId: "node-staging-1",
    compose: STOREFRONT_COMPOSE,
    createdBy: "founder",
    createdAt: hoursAgo(5),
    updatedAt: hoursAgo(1),
  },
];

export const CONTAINERS: Container[] = [
  {
    id: "c-storefront-web",
    name: "storefront-web",
    image: "nginx:1.27",
    state: "running",
    status: "Up 3 days",
    createdAt: daysAgo(5),
    startedAt: daysAgo(3),
    fleetId: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    stackId: "stack-storefront",
    stackName: "storefront",
    ports: [{ containerPort: 80, protocol: "tcp", hostPort: 8080 }],
    labels: { "com.comebye.service": "web" },
  },
  {
    id: "c-storefront-api",
    name: "storefront-api",
    image: "ghcr.io/acme/api:2.4.1",
    state: "running",
    status: "Up 3 days",
    createdAt: daysAgo(5),
    startedAt: daysAgo(3),
    fleetId: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    stackId: "stack-storefront",
    stackName: "storefront",
    ports: [{ containerPort: 3000, protocol: "tcp", hostPort: 3000 }],
    labels: { "com.comebye.service": "api" },
  },
  {
    id: "c-storefront-worker",
    name: "storefront-worker",
    image: "ghcr.io/acme/worker:2.4.1",
    state: "restarting",
    status: "Restarting (1) 12 seconds ago",
    createdAt: daysAgo(5),
    startedAt: minutesAgo(1),
    fleetId: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    stackId: "stack-storefront",
    stackName: "storefront",
    ports: [],
    labels: { "com.comebye.service": "worker" },
  },
  {
    id: "c-adhoc-debug-shell",
    name: "adhoc-debug-shell",
    image: "alpine:3.20",
    state: "exited",
    status: "Exited (0) 4 hours ago",
    createdAt: hoursAgo(4),
    startedAt: hoursAgo(4),
    fleetId: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    stackId: null,
    stackName: null,
    ports: [],
    labels: {},
  },
  {
    id: "c-observability-prometheus",
    name: "observability-prometheus",
    image: "prom/prometheus:v2.53.0",
    state: "running",
    status: "Up 2 days",
    createdAt: daysAgo(2),
    startedAt: daysAgo(2),
    fleetId: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    stackId: "stack-observability",
    stackName: "observability",
    ports: [{ containerPort: 9090, protocol: "tcp", hostPort: 9090 }],
    labels: { "com.comebye.service": "prometheus" },
  },
  {
    id: "c-observability-grafana",
    name: "observability-grafana",
    image: "grafana/grafana:11.1.0",
    state: "paused",
    status: "Up 2 days (Paused)",
    createdAt: daysAgo(2),
    startedAt: daysAgo(2),
    fleetId: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    stackId: "stack-observability",
    stackName: "observability",
    ports: [{ containerPort: 3000, protocol: "tcp", hostPort: 3001 }],
    labels: { "com.comebye.service": "grafana" },
  },
  {
    id: "c-observability-loki",
    name: "observability-loki",
    image: "grafana/loki:3.1.0",
    state: "exited",
    status: "Exited (137) 2 hours ago",
    createdAt: daysAgo(2),
    startedAt: hoursAgo(2),
    fleetId: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    stackId: "stack-observability",
    stackName: "observability",
    ports: [],
    labels: { "com.comebye.service": "loki" },
  },
  {
    id: "c-edge-traefik",
    name: "edge-traefik",
    image: "traefik:v3.1",
    state: "dead",
    status: "Dead — node unreachable",
    createdAt: daysAgo(10),
    startedAt: null,
    fleetId: "fleet-prod",
    nodeId: "node-prod-edge-1",
    nodeName: "prod-edge-1",
    stackId: "stack-edge-proxy",
    stackName: "edge-proxy",
    ports: [{ containerPort: 443, protocol: "tcp", hostPort: 443 }],
    labels: { "com.comebye.service": "traefik" },
  },
  {
    id: "c-staging-web",
    name: "staging-web",
    image: "nginx:1.27",
    state: "running",
    status: "Up 5 hours",
    createdAt: hoursAgo(5),
    startedAt: hoursAgo(5),
    fleetId: "fleet-staging",
    nodeId: "node-staging-1",
    nodeName: "staging-1",
    stackId: "stack-storefront-staging",
    stackName: "storefront-staging",
    ports: [{ containerPort: 80, protocol: "tcp", hostPort: 8080 }],
    labels: { "com.comebye.service": "web" },
  },
  {
    id: "c-staging-api",
    name: "staging-api",
    image: "ghcr.io/acme/api:2.5.0-rc1",
    state: "running",
    status: "Up 5 hours",
    createdAt: hoursAgo(5),
    startedAt: hoursAgo(5),
    fleetId: "fleet-staging",
    nodeId: "node-staging-1",
    nodeName: "staging-1",
    stackId: "stack-storefront-staging",
    stackName: "storefront-staging",
    ports: [{ containerPort: 3000, protocol: "tcp", hostPort: 3000 }],
    labels: { "com.comebye.service": "api" },
  },
];

export const SECRETS: SecretSummary[] = [
  {
    id: "secret-dockerhub-pull",
    name: "dockerhub-pull",
    version: 1,
    fleetId: "fleet-prod",
    createdBy: "founder",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
  },
  {
    id: "secret-github-deploy-key",
    name: "github-deploy-key",
    version: 3,
    fleetId: "fleet-prod",
    createdBy: "founder",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(12),
  },
  {
    id: "secret-staging-dockerhub-pull",
    name: "dockerhub-pull",
    version: 1,
    fleetId: "fleet-staging",
    createdBy: "founder",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
  },
];

/**
 * Environment-wide (not fleet-scoped, like the lists above). `usr-admin`'s
 * id is what `mockClient.ts`'s stub `login()`/`getAccount()` uses as "you" —
 * see the note there — so the "can't remove/deactivate yourself" guard has
 * something real to demo against.
 */
export const USERS: User[] = [
  {
    id: "usr-admin",
    username: "admin",
    displayName: "Founder Admin",
    role: "admin",
    active: true,
    createdAt: daysAgo(120),
    lastLoginAt: minutesAgo(3),
  },
  {
    id: "usr-ops",
    username: "ops",
    displayName: "Ops Operator",
    role: "operator",
    active: true,
    createdAt: daysAgo(60),
    lastLoginAt: hoursAgo(5),
  },
  {
    id: "usr-viewer",
    username: "viewer",
    displayName: "Read-only Viewer",
    role: "viewer",
    active: true,
    createdAt: daysAgo(45),
    lastLoginAt: daysAgo(1),
  },
  {
    id: "usr-ex-contractor",
    username: "ex-contractor",
    displayName: "Former Contractor",
    role: "operator",
    active: false,
    createdAt: daysAgo(200),
    lastLoginAt: daysAgo(40),
  },
];

/** Action/resource/outcome templates cycled to build a believable, varied
 * synthetic trail — not meant to be exhaustive, just plausible enough to
 * exercise the audit viewer's table, pagination, and outcome styling. */
const AUDIT_TEMPLATES: { action: string; resource: string; outcome: string }[] = [
  { action: "login.success", resource: "session", outcome: "success" },
  { action: "login.failure", resource: "session", outcome: "failure" },
  { action: "secret.set", resource: "secret", outcome: "success" },
  { action: "secret.rotate", resource: "secret", outcome: "success" },
  { action: "secret.delete", resource: "secret", outcome: "success" },
  { action: "stack.deploy", resource: "stack", outcome: "success" },
  { action: "stack.redeploy", resource: "stack", outcome: "success" },
  { action: "container.exec", resource: "container", outcome: "success" },
  { action: "container.restart", resource: "container", outcome: "success" },
  { action: "node.enroll", resource: "node", outcome: "success" },
  { action: "docker.deny", resource: "container", outcome: "deny" },
  { action: "user.update", resource: "user", outcome: "success" },
];

const AUDIT_ACTORS: { userId: string; role: string }[] = [
  { userId: "usr-admin", role: "admin" },
  { userId: "usr-ops", role: "operator" },
];

/**
 * Builds a believable, newest-first synthetic trail for one fleet — enough
 * rows (default 130) to make the audit viewer's cursor pagination worth
 * exercising. Deterministic per fleet (no `Math.random`) so the page count
 * and rows a demo sees are stable across reloads.
 */
export function buildAuditEvents(fleetId: string, count = 130): AuditEvent[] {
  const events: AuditEvent[] = [];
  for (let i = 0; i < count; i++) {
    const seq = count - i;
    const template = AUDIT_TEMPLATES[i % AUDIT_TEMPLATES.length];
    const actor = AUDIT_ACTORS[i % AUDIT_ACTORS.length];
    events.push({
      id: `aud-mock-${fleetId}-${seq}`,
      seq,
      occurredAt: minutesAgo(i * 41 + 3),
      actorUserId: actor.userId,
      actorRole: actor.role,
      action: template.action,
      resource: template.resource,
      target: `${template.resource}-${(seq % 9) + 1}`,
      outcome: template.outcome,
      fleetId,
      prevHash: `h${seq - 1}`,
      hash: `h${seq}`,
    });
  }
  return events;
}

/*
 * ---- Hero flow: projects / services / deployments / provider credentials /
 * instances ---- all scoped to fleet-prod, mirroring the real, merged
 * backend contract (#63 projectsvc, #62 provisioning) exactly. Two things
 * that are deliberately NOT varied here because they can't be, honestly:
 *   - every `Service.status` below is `"created"` — the only value the real
 *     server has ever set (deploying isn't implemented, see DEPLOYMENTS);
 *   - DEPLOYMENTS is empty — nothing can create a deployment row while
 *     `TriggerDeployHandler` answers 501.
 */

export const PROVIDER_CREDENTIALS: ProviderCredentialSummary[] = [
  {
    id: "provcred-hetzner-prod",
    kind: "hetzner",
    label: "Hetzner — prod project",
    fleetId: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
  },
];

export const INSTANCES: Instance[] = [
  {
    id: "inst-hetzner-web-1",
    fleetId: "fleet-prod",
    projectId: "proj-storefront",
    providerCredId: "provcred-hetzner-prod",
    providerKind: "hetzner",
    externalId: "hetzner-42",
    region: "fsn1",
    serverType: "cx22",
    status: "running",
    publicIpv4: "203.0.113.10",
    agentId: "node-prod-web-1",
    createdBy: "admin",
    createdAt: daysAgo(28),
    updatedAt: daysAgo(28),
  },
  {
    id: "inst-hetzner-provisioning",
    fleetId: "fleet-prod",
    projectId: "proj-storefront",
    providerCredId: "provcred-hetzner-prod",
    providerKind: "hetzner",
    externalId: "",
    region: "fsn1",
    serverType: "cx22",
    status: "provisioning",
    publicIpv4: "",
    createdBy: "admin",
    createdAt: minutesAgo(3),
    updatedAt: minutesAgo(3),
  },
];

export const PROJECTS: Project[] = [
  {
    id: "proj-storefront",
    name: "Storefront",
    description: "Customer-facing web app and API",
    fleetId: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "proj-internal-tools",
    name: "Internal tools",
    description: "Admin dashboards and one-off scripts",
    fleetId: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(9),
  },
];

export const SERVICES: Service[] = [
  {
    id: "svc-storefront-web",
    projectId: "proj-storefront",
    fleetId: "fleet-prod",
    name: "web",
    image: "ghcr.io/acme/storefront-web:1.4.0",
    env: { NODE_ENV: "production" },
    secretRefs: ["storefront-db-url"],
    desiredCount: 2,
    status: "created",
    createdBy: "admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "svc-storefront-worker",
    projectId: "proj-storefront",
    fleetId: "fleet-prod",
    name: "worker",
    image: "ghcr.io/acme/storefront-worker:1.4.0",
    env: {},
    secretRefs: ["storefront-db-url"],
    desiredCount: 1,
    status: "created",
    createdBy: "admin",
    createdAt: daysAgo(5),
    updatedAt: daysAgo(5),
  },
];

// Always empty: nothing can create a row while TriggerDeployHandler answers
// 501 — see the header note above. Kept as a typed, exported empty array
// (not just omitted) so the mock's "always empty" behavior is visible at a
// glance next to PROJECTS/SERVICES, rather than looking like an oversight.
export const DEPLOYMENTS: Deployment[] = [];
